import { Context } from "@/context";
import { instrumentDiceRoll } from "@/helpers/bi/instrumentDiceRoll";
import { getPlatform } from "@/helpers/getPlatform";
import { rollDice } from "@/helpers/rollDice";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { RollDicePayload, RollKind, RollType } from "@/types";
import {
  autoPlacement,
  autoUpdate,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import clsx from "clsx";
import {
  useContext,
  useRef,
  useState,
  type Dispatch,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type PropsWithChildren,
  type SetStateAction,
} from "react";
import { RollContextMenu } from "./components/RollContextMenu";
import styles from "./styles.module.css";

export interface RollableProps extends PropsWithChildren {
  className?: string;
  diceNotation: string;
  action: string;
  entityId: string;
  entityType: string;
  dmId?: string;
  gameId?: string;
  name: string;
  rollKind?: RollKind;
  rollType?: RollType;
  isContextMenuEnabled?: boolean;
  isDiceEnabled?: boolean;
  setRollData?: Dispatch<SetStateAction<RollDicePayload["data"]>>;
}

export const Rollable: FC<RollableProps> = ({
  children,
  className,
  diceNotation,
  action,
  entityId,
  entityType,
  dmId,
  gameId,
  name,
  rollKind,
  rollType,
  isContextMenuEnabled = true,
  isDiceEnabled = true,
  setRollData,
}) => {
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isLongPressTriggeredRef = useRef(false);
  const isKeyboardClickRef = useRef(false);
  const { refs, floatingStyles, context } = useFloating({
    strategy: "fixed",
    transform: false,
    open: isContextMenuOpen,
    middleware: [
      autoPlacement({
        allowedPlacements: [
          "left",
          "left-start",
          "left-end",
          "right",
          "right-start",
          "right-end",
        ],
      }),
    ].filter(Boolean),
    whileElementsMounted: autoUpdate,
    onOpenChange(open, _, reason) {
      if (reason === "escape-key" || reason === "outside-press") {
        setIsContextMenuOpen(open);
      }
    },
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const handleContextMenu = (e: MouseEvent) => {
    if (!isContextMenuEnabled) return e;

    e.preventDefault();
    e.stopPropagation();
    setIsContextMenuOpen(true);
  };
  const clearLongPressTimeout = () => {
    if (!longPressTimeoutRef.current) return;

    clearTimeout(longPressTimeoutRef.current);

    longPressTimeoutRef.current = null;
  };
  const { user, hasPddErrored, isPddReady, env } = useContext(Context);
  const setId = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.setId,
  );
  const triggerRoll = () => {
    const { data } = rollDice(
      {
        diceNotation,
        action,
        entityId,
        entityType,
        setId: String(setId),
        avatarUrl: user?.avatarUrl,
        dmId,
        gameId,
        name,
        rollKind,
        rollType,
        userId: String(user?.id),
      },
      hasPddErrored,
      isPddReady,
      (payload) => {
        instrumentDiceRoll(
          user!,
          payload,
          getPlatform(),
          "characterSheet",
          gameId ? Number(gameId) : undefined,
          undefined,
          env,
        );
      },
    );

    setRollData?.(data);
  };
  const handlePointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    isKeyboardClickRef.current = false;

    if (e.pointerType === "mouse" && e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    if (isContextMenuOpen) {
      setIsContextMenuOpen(false);

      return;
    }

    if (e.pointerType === "mouse") {
      triggerRoll();

      return;
    }

    isLongPressTriggeredRef.current = false;

    clearLongPressTimeout();

    longPressTimeoutRef.current = setTimeout(() => {
      isLongPressTriggeredRef.current = true;

      setIsContextMenuOpen(true);
    }, 650);
  };
  const handlePointerUp = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === "mouse") return;

    e.preventDefault();
    e.stopPropagation();
    clearLongPressTimeout();

    if (isLongPressTriggeredRef.current) {
      isLongPressTriggeredRef.current = false;

      return;
    }

    triggerRoll();
  };
  const handlePointerCancel = () => {
    clearLongPressTimeout();

    isLongPressTriggeredRef.current = false;
  };
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    // Pointer interactions are handled in pointer handlers.
    if (!isKeyboardClickRef.current) {
      return;
    }

    isKeyboardClickRef.current = false;

    if (isContextMenuOpen) {
      setIsContextMenuOpen(false);

      return;
    }

    triggerRoll();
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      isKeyboardClickRef.current = true;
    }

    if (!isContextMenuEnabled) return;

    if (e.key === "ContextMenu" || (e.key === "F10" && e.shiftKey)) {
      e.preventDefault();
      e.stopPropagation();
      setIsContextMenuOpen(true);
    }
  };

  if (!isDiceEnabled) return children;

  return (
    <>
      <button
        type="button"
        className={clsx([styles.button, className])}
        onContextMenu={handleContextMenu}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerCancel}
        aria-haspopup="menu"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {children}
      </button>
      {isContextMenuEnabled && isContextMenuOpen && (
        <FloatingPortal>
          <RollContextMenu
            // eslint-disable-next-line react-hooks/refs -- false positive, bug in lint rule
            ref={refs.setFloating}
            style={floatingStyles}
            diceNotation={diceNotation}
            setIsOpen={setIsContextMenuOpen}
            action={action}
            entityId={entityId}
            entityType={entityType}
            dmId={dmId}
            gameId={gameId}
            name={name}
            rollKind={rollKind}
            rollType={rollType}
            setRollData={setRollData}
            {...getFloatingProps()}
          />
        </FloatingPortal>
      )}
    </>
  );
};
